import {
  Component,
  OnInit,
  OnChanges,
  Input,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef
} from "@angular/core";
import { MonitoredTrain } from "../../models/monitored-train";
import { ReferenceArea } from "../../../reference-areas/models/reference-area";
import { environment } from "../../../../../environments/environment";
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { UserProfileService } from '../../../users/services/user-profile.service';
import {MonitoringHost} from '../../../project/models/project-configuration';
import {MonitoringZabbixHost} from "@/app/features/monitoring/models/monitoring-zabbix-host";

declare const google; 

@Component({
  selector: "app-monitored-trains-map",
  template: require('./monitored-trains-map.component.html'),
  styles: [require('./monitored-trains-map.component.sass')],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonitoredTrainsMapComponent implements OnInit, OnChanges {
  @Input() projectId: number;
  @Input() trains: MonitoredTrain[];
  @Input() zoom: number | undefined;
  @Input() showTrainState: boolean;
  @Input() markOfflineTrain: boolean;
  @Input() isSingleTrain: boolean = false;
  @Input() isShortMap: boolean = false;
  @Input() hideSearch: boolean = false;
  @Input() hostObjects: MonitoringZabbixHost[];
  @Input() referenceAreas: ReferenceArea[];
  @Input() mapClass: string;
  @Input() mapLegendPosition: string;
  @Input() asset: string = 'train';
  @Output() investigateTrain = new EventEmitter<MonitoredTrain>();
  @Output() reload = new EventEmitter();
  @Output() onAssetSearch = new EventEmitter<{
    asset: string;
    keyword: string;
  }>();

  @ViewChild('search') searchElement: ElementRef;

  searchTrain: string;
  searchHost: string;
  legends: any[];
  drawingOptions = {
    drawingMode: null,
    drawingControl: false,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER,
      drawingModes: [google.maps.drawing.OverlayType.POLYGON]
    },
    polygonOptions: {
      strokeOpactity: 0.8,
      strokeColor: "#FF5757",
      fillColor: "#FF5757",
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: true,
      editable: false,
      zIndex: 1
    }
  };
  drawingManager = new google.maps.drawing.DrawingManager(this.drawingOptions);
  map: any;

  showPolygons = true;
  polygonInfoWindow = new google.maps.InfoWindow();

  selectedReferenceArea: any;
  activePolygon: any;

  defaultLocation = {
    lat: 54.5260,
    lng: 15.2551
  }

  currentMapCenter: {
    lat: number,
    lng: number,
  } | null;

  selectedTrains: MonitoredTrain[] | null;
  selectedHosts: MonitoringZabbixHost[] | null;
  selectedTrainUniqueId: string | null;

  defaultZoom: number;

  trainsMap: any;

  constructor(
    private referenceAreaDataService: ReferenceAreaDataService,
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService,
    private messageService: MessageService,
    private userProfileService: UserProfileService
  ) {}

  ngOnInit(): void {
    this.drawingManager.setMap(null);
  }

  ngOnChanges(): void {
    if (this.trains && this.trains.length > 1) {
      this.drawingManager.setOptions({
          drawingControl: this.userProfileService.isAdmin()
       });
    } 

    if (this.trains && this.trains.length === 0) {
      this.zoom = 4;
    }
    this.legends = [];
    if (this.referenceAreas) {
      const referenceAreaTypes = this.referenceAreas.reduce(
        (acc, reference) => {
          acc[reference.type.toLowerCase()] = true;
          return acc;
        },
        {}
      );
      this.legends = [
        ...this.legends,
        ...Object.keys(referenceAreaTypes)
          .map(type => {
            const typeLegend = environment.config.map.legends.find(legend =>
              legend.name.includes(type.toLowerCase())
            );
            return typeLegend;
          })
          .filter(legend => !!legend)
      ];
    }

    let mapLegend = localStorage.getItem('map-legend');
    if (mapLegend) {
      const parsedMapLegend: any[] = JSON.parse(mapLegend);
      this.legends = this.legends.map(legend => {
        const currentLegend = parsedMapLegend && parsedMapLegend.find(localLegend => localLegend.name === legend.name);
        return currentLegend ? {
          ...legend,
          ...currentLegend
        } : legend
      })
    } else {
      localStorage.setItem('map-legend', JSON.stringify(this.legends));
    }

    

    this.referenceAreas = this.referenceAreas
      ? this.referenceAreas
          .map(referenceArea => this.processReferenceArea(referenceArea))
          .sort((p1, p2) => (p1.surface > p2.surface ? -1 : 1))
      : [];

    if (this.trains && this.trains.length === 1) {
      this.currentMapCenter = {
        lat: this.trains[0].currentPosition.latitude,
        lng: this.trains[0].currentPosition.longitude
      }
    }

    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
  }

  processReferenceArea = (referenceArea: ReferenceArea) => {
    const paths = referenceArea.area && referenceArea.area.positions ? referenceArea.area.positions.map(pos => {
      return new google.maps.LatLng(pos.latitude, pos.longitude);
    }) : [];
    const bounds = new google.maps.LatLngBounds();
    paths.forEach(path => {
      bounds.extend(path);
    });
    return {
      ...referenceArea,
      surface: google.maps.geometry.spherical.computeArea(paths),
      polygonCenter: bounds.getCenter(),
      paths
    };
  }

  zoomChange = (zoom: number) => {
    this.zoom = zoom;
  }

  onPolyClick = async (referenceArea: any) => {
    this.selectedReferenceArea = { ...referenceArea };
  };

  onSelectedReferenceAreaClose = () => {
    this.selectedReferenceArea = null;
  };

  onInvestigateTrain = (train: MonitoredTrain) => {
    this.investigateTrain.emit(train);
  };

  onMouseClick(infoWindow, train: MonitoredTrain) {
    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
    const highestZIndex = Object.keys(this.trainsMap).reduce((acc, trainKey) => Math.max(acc, this.trainsMap[trainKey]?.zIndex) , 0);
    this.trainsMap[train.uid] = {
      zIndex: highestZIndex + 1
    }
    localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));
    infoWindow.open();
  }

  trainInfoClose = (trainUid: string) => {
    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
    this.trainsMap[trainUid] = undefined;
    localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));
  }

  onMapReady(map) {
    this.map = map;
    this.drawingManager.setMap(this.map);
    google.maps.event.addListener(
      this.drawingManager,
      "polygoncomplete",
      this.onPolygonDrawingComplete
    );
  }

  onPolygonDrawingComplete = (polygon: any) => {
    this.activePolygon = polygon;
    this.selectedReferenceArea = null;
    this.drawingManager.setDrawingMode(null);
    const positions: any[] = [];
    polygon.getPath().forEach((LatLng: any) => {
      positions.push({
        latitude: LatLng.lat(),
        longitude: LatLng.lng()
      });
    });
    let newReferenceArea: any = {
      area: {
        positions
      }
    };
    const paths = newReferenceArea.area.positions.map(pos => {
      return new google.maps.LatLng(pos.latitude, pos.longitude);
    });
    const bounds = new google.maps.LatLngBounds();
    paths.forEach(path => {
      bounds.extend(path);
    });
    this.selectedReferenceArea = {
      ...newReferenceArea,
      surface: google.maps.geometry.spherical.computeArea(paths),
      polygonCenter: bounds.getCenter(),
      paths,
      projectId: this.projectId,
      latLongList: newReferenceArea.area && newReferenceArea.area.positions ? newReferenceArea.area.positions.reduce((acc, position) => {
        return acc ? acc + " " + position.latitude + "," +position.longitude :  position.latitude + "," +position.longitude
      }, '') : ''
    };

  };


  onSaveReferenceArea = async (referenceArea: ReferenceArea) => {
    try {
      await this.referenceAreaDataService.saveReferenceAreas(this.projectId, [referenceArea]);
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant('I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA'),
        detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${referenceArea.id ? this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED") : this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")}`
      });
      this.reload.emit();
    } catch(error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant("I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA"),
        detail: error && error.message
      });
    }

    this.selectedReferenceArea = null;
    if (!referenceArea.id) {
      this.activePolygon.setMap(null);
    }
  }

  onCancelReferenceArea = () => {
    this.selectedReferenceArea = null;
    this.activePolygon.setMap(null);
  }

  onDeleteReferenceArea = async (referenceArea: ReferenceArea) => {
    if (referenceArea && referenceArea.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        `${this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA')} ${referenceArea.description}`,
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE_CONFIRMATION'),
        'danger',
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA'),
        this.translate.instant('I18N_GENERAL.CANCEL'),
        "lg");
      if (confirmed) {
        try {
          await this.referenceAreaDataService.deleteReferenceArea(referenceArea.projectId, referenceArea.id, referenceArea.type);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA'),
            detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.reload.emit();
        } catch (error) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA"),
            detail: error && error.message
          });
        }
      }
    }
    this.selectedReferenceArea = null;
  }

  toggleLegendItem = item => {
    item.visible = !item.visible;
    this.legends = this.legends.map(legend => {
      if (legend.name === item.name) {
        legend = {
          ...item
        }
      }
      return legend;
    })
    localStorage.setItem('map-legend', JSON.stringify(this.legends));
  }

  uniqueTrainIdentifier = (index: number, train: MonitoredTrain): string => {
    return train.projectId + '-' + train.trainId;
  }

  onTrainSearch = () => {
    this.trainsMap = {};
    localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));

    if (!this.defaultLocation) {
      this.defaultLocation = {
        lat: this.map.center.lat(),
        lng: this.map.center.lng()
      }
      this.defaultZoom = this.map.zoom
    }
    this.selectedTrains =  this.searchTrain ? this.trains && [
      ...this.trains.filter(train => {
      return (train.name && train.name.toString().toLowerCase() === (String(this.searchTrain).toLowerCase()))
    }),
    ...this.trains.filter(train => {
      return (train.trainId && train.trainId.toString().toLowerCase() === (String(this.searchTrain).toLowerCase()))
    }),
  ]: this.trains;

    if (this.searchTrain) {
      if (this.selectedTrains && this.selectedTrains.length > 0) {
        this.currentMapCenter = {
          lat: this.selectedTrains[0].currentPosition.latitude,
          lng: this.selectedTrains[0].currentPosition.longitude
        }
        this.zoom = 10;
        this.map.panTo(new google.maps.LatLng(this.currentMapCenter.lat,this.currentMapCenter.lng))
        this.selectedTrainUniqueId = this.selectedTrains[0].projectId + '-' + this.selectedTrains[0].trainId
        this.trainsMap[this.selectedTrainUniqueId] = {
          zIndex: 1
        }
        localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));
        setTimeout(()=>{ // this will make the execution after the above boolean has changed
          this.searchElement.nativeElement.focus();
        },0); 

      } else {
        this.currentMapCenter = null;
        this.zoom = this.defaultZoom;
        this.selectedTrains = null;
        this.selectedTrainUniqueId = null;
        this.messageService.clear();
        this.messageService.add({
          severity:'error',
          summary: this.translate.instant("I18N_MONITORING.NO_SINGLE_TRAIN_MATCHED"),
          detail: this.translate.instant("I18N_MONITORING.NO_SINGLE_TRAIN_MATCHED_DESCRIPTION")
        });
      }
    } else {
      this.selectedTrainUniqueId = null;
      this.zoom = this.defaultZoom;
    }


  }

  onHostSearch = ()=> {
    if (!this.defaultLocation) {
      this.defaultLocation = {
        lat: this.map.center.lat(),
        lng: this.map.center.lng()
      }
      this.defaultZoom = this.map.zoom
    }
      this.selectedHosts =  this.searchHost ? this.hostObjects && [
        ...this.hostObjects.filter(host => {
          return (host.hostName && host.hostName.toString().toLowerCase() === (String(this.searchHost).toLowerCase()))
        }),
      ]: this.hostObjects;

      if (this.searchHost) {
        if (this.selectedHosts && this.selectedHosts.length > 0) {
          this.currentMapCenter = {
            lat: this.selectedHosts[0].latitude,
            lng: this.selectedHosts[0].longitude
          }
          this.map.panTo(new google.maps.LatLng(this.currentMapCenter.lat,this.currentMapCenter.lng))
          this.zoom = 10;
          setTimeout(()=>{ // this will make the execution after the above boolean has changed
            this.searchElement.nativeElement.focus();
          },0);

          this.onAssetSearch.emit({
            asset: this.asset,
            keyword: this.searchHost
          })

        } else {
          this.currentMapCenter = null;
          this.zoom = this.defaultZoom;
          this.selectedHosts = null;
          this.messageService.clear();
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_MONITORING.NO_SINGLE_HOST_MATCHED"),
            detail: this.translate.instant("I18N_MONITORING.NO_SINGLE_HOST_MATCHED_DESCRIPTION")
          });
        }
      } else {
        this.zoom = this.defaultZoom;
        this.onAssetSearch.emit({
          asset: this.asset,
          keyword: this.searchHost
        })
      }
    }
}